import type { PlanTablePositionAnchor } from './positionAnchor'

export type PlanType = 'PositionPlan' | 'StiffeningPlan' | 'LoadPlan'

export type PlanTableType = 'CustomPlanTable' | 'TemplatePlanTable'

export type { PlanTablePositionAnchor }

export interface PlanTapelineConfig {
  storeysForPlans: string[]
  planTypes: PlanType[]
}

export type PlanTableSortDirection = 'asc' | 'desc'

/** Orders the body rows of a table by the text of one column (in the plan and its preview). */
export interface PlanTableSorting {
  columnKey: string
  direction: PlanTableSortDirection
}

export interface PositionListElementTypeVisibility {
  columns: boolean
  beams: boolean
  purlins: boolean
  lintels: boolean
  rips: boolean
  verticalSlabs: boolean
  verticalRoofSlabs: boolean
}

export type PositionListTemplateOptions = {
  onlyShowElementsWithComment: boolean
  elementTypes: PositionListElementTypeVisibility
}

export type TensileAnchorsTemplateOptions = {
  showTotalAnchorCount: boolean
  showStoreyAnchorCount: boolean
}

export type ShearAnchorsTemplateOptions = {
  showStoreyAnchorCount: boolean
}

export interface PlanTableTemplateOptions {
  onlyShowElementsWithComment?: boolean
  elementTypes?: PositionListElementTypeVisibility
  showTotalAnchorCount?: boolean
  showStoreyAnchorCount?: boolean
  [key: string]: unknown
}

export interface TableCellSettings {
  horizontalAlignment?: 'left' | 'center' | 'right'
  verticalAlignment?: 'top' | 'middle' | 'bottom'
  textColorHex?: string
  backgroundColorHex?: string | null
}

export interface TableColumn {
  key: string
  header?: string | null
}

export interface TableCell {
  columnKey: string
  value: string[]
  settings?: TableCellSettings | null
}

export interface TableRow {
  key: string
  cells: TableCell[]
}

export interface TableSettings {
  defaultCellSettings?: TableCellSettings
  headerCellSettings?: TableCellSettings
  borderColorHex?: string
  borderWidth?: number
  cellTextMargin?: number
}

export interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
  headerRow?: TableRow | null
  settings: TableSettings
}

interface PlanTableBase {
  id: string
  name: string
  position: { x: number; y: number; z: number }
  /**
   * Which corner of the table `position` refers to. Defaults to top-left.
   */
  positionAnchor: PlanTablePositionAnchor
  filterViewId: string
  table: TableData
  isVisible: boolean
  isActive: boolean
  planConfig: PlanTapelineConfig
  sorting?: PlanTableSorting | null
  templateOptions?: PlanTableTemplateOptions
  dateCreated: string
  dateModified: string
}

export interface CustomPlanTable extends PlanTableBase {
  tableType: 'CustomPlanTable'
  templateKey?: null
}

export interface TemplatePlanTable extends PlanTableBase {
  tableType: 'TemplatePlanTable'
  templateKey: string
}

export type PlanTable = CustomPlanTable | TemplatePlanTable

export const isTemplatePlanTable = (planTable: PlanTable): planTable is TemplatePlanTable =>
  planTable.tableType === 'TemplatePlanTable'

export interface PlanTableTemplate {
  key: string
  name: string
  defaultName: string
}

export type PlanTableInput = Partial<PlanTable> & {
  position: { x: number; y: number; z: number }
}
